/**
 * Chat image client-side resize/compress constants.
 *
 * Source of truth: the two ffmpeg scale passes the chat-image Cloud Function
 * USED to run server-side, now moved onto the client (story Context — chat
 * media is client-side E2EE so the server can no longer see plaintext to
 * resize). Pre-rework `cloud_functions/.../transcoding/transcode_chat_image.ts`:
 *   - Full image (`:87`):
 *       `scale=w='if(gt(iw,ih),-2,720)':h='if(gt(iw,ih),720,-2)':force_original_aspect_ratio=decrease`
 *     → longest side = 720, aspect ratio preserved, never upscale.
 *   - Thumbnail (`:123`):
 *       `scale=w='if(gt(iw,ih),-2,480)':h='if(gt(iw,ih),480,-2)':force_original_aspect_ratio=decrease`
 *     → longest side = 480, aspect ratio preserved, never upscale.
 *
 * `force_original_aspect_ratio=decrease` means ffmpeg only ever shrinks the
 * image to fit the box (a smaller source is left at its native size). The
 * canvas resize approximates this with `min(1, target / longestSide)`.
 *
 * The old ffmpeg passes wrote JPEG (`contentType: 'image/jpeg'`,
 * `tempLocalImageFile`/`tempLocalThumbnailFile` are `.jpg`); the canvas export
 * keeps `image/jpeg` so the produced bytes match the legacy content type. The
 * quality values are NOT ported from ffmpeg's commented-out `-q:v` flags (those
 * were disabled) — they are sensible JPEG defaults for inline chat media.
 */
export const CHAT_IMAGE_PROCESSING_CONSTANTS = {
  /** Longest-side target (px) for the full chat image (ffmpeg `:87`, was 720). */
  FULL_IMAGE_MAX_SIDE: 720,
  /** Longest-side target (px) for the chat thumbnail (ffmpeg `:123`, was 480). */
  THUMBNAIL_MAX_SIDE: 480,
  /** JPEG quality for the full image export (`canvas.toBlob` quality arg). */
  FULL_IMAGE_JPEG_QUALITY: 0.85,
  /** JPEG quality for the thumbnail export. */
  THUMBNAIL_JPEG_QUALITY: 0.7,
  /** Output MIME — matches the legacy server `contentType: 'image/jpeg'`. */
  OUTPUT_MIME_TYPE: 'image/jpeg',
  /**
   * Sub-folder (under the chat upload dir) the encrypted FULL image ciphertext
   * is uploaded to. MUST match the CF-side `uploadImageFolder = 'image/'` in
   * the reworked `transcode_chat_image.ts` (Task 5 path contract).
   */
  IMAGE_SUBFOLDER: 'image/',
  /**
   * Sub-folder the encrypted THUMBNAIL ciphertext is uploaded to. MUST match
   * the CF-side `uploadThumbnailFolder = 'thumbnail/'`.
   */
  THUMBNAIL_SUBFOLDER: 'thumbnail/',
} as const;

export type ChatImageProcessingConstants = typeof CHAT_IMAGE_PROCESSING_CONSTANTS;
